import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '@features/booking/state';
import { useBookingFlow, BOOKING_STEPS } from '@features/booking/flow';
import { hasActiveBookingSession } from '@features/booking/utils/bookingSession';
import { markFullStoryEvent } from '@core/analytics/fullstory';
import type { BookingStepId } from '@features/booking/flow';

/**
 * Keep a guest from being stranded on a step that is not part of their flow.
 *
 * `activeSteps` is derived from live booking state, so a page can be mounted on
 * a path that is not in it: a stale bookmark, a shared link, browser history
 * after the flow moved on, or the page's own state change removing its step.
 * The pages in that position call `goToNext()` from a click handler or a mount
 * effect — which resolves null for a non-active path and silently returns. The
 * guest gets a button that does nothing, or a blank render.
 *
 * Two situations, two answers:
 *
 * - **Mid-session** (the guest has real flow progress): advance from the step's
 *   canonical position via `goToNextFromStep`. Safe, because every step before
 *   this one was already satisfied to get here.
 * - **Cold landing** (no session at all): do NOT advance. Forward from a late
 *   step would drop them deep in the flow having never chosen diners, date, or
 *   time. Send them to the start; `replace` so Back does not bounce them
 *   straight back into the dead step.
 *
 * Deliberately not a general route guard: it fires only for a step that is
 * genuinely inactive, and it never advances past a step the guest has not met.
 * The dead-end telemetry lives in `navigateToNextStep` and still fires for
 * anything this does not catch.
 */
export function useInactiveStepRecovery(stepId: BookingStepId): void {
  const navigate = useNavigate();
  const booking = useBooking();
  const { activeSteps, goToNextFromStep, goToStep } = useBookingFlow();

  const isActive = activeSteps.some((s) => s.id === stepId);
  const hasSession = hasActiveBookingSession(booking);

  // `resolveNextStepAfter` is forward-only, and its contract requires the
  // caller to have satisfied every step at or before its own — otherwise the
  // jump SKIPS those steps. `hasActiveBookingSession` does not establish that:
  // it is an any-progress-at-all predicate borrowed from a Sentry severity
  // classifier, where erring toward "warm" is the safe direction. Here the risk
  // inverts, so it is necessary but not sufficient.
  //
  // TIME is unconditionally required (`isRequired: () => true`), so it is the
  // one ALWAYS-active step the wired call sites can sit after — the only one
  // whose skip is guaranteed reachable. (SIGN_IN and COMPLETE_PROFILE are also
  // canonically before MARIDAJE and can be active; a warm guest who lost auth
  // independently would still advance past them. Not gated here because that
  // outcome is not silent: runGuards' token guard dispatches `auth:expired`
  // and AuthProvider.logout() puts the flow back on its feet.) Concretely:
  // change
  // party size, date, or service and BookingProvider clears `selectedTime`
  // while `selectedDate` survives — so a Forward-button return to /maridaje
  // reads as "warm", advances past TIME, and lands on /confirmation where
  // `runGuards` rejects the commit SILENTLY. That is the same dead-end class,
  // one page deeper and harder to back out of.
  //
  // Steps canonically at or before TIME are safe: advancing from them lands on
  // TIME or an adjacent later step, skipping nothing the guest still owes.
  const stepIdx = BOOKING_STEPS.findIndex((s) => s.id === stepId);
  const timeIdx = BOOKING_STEPS.findIndex((s) => s.id === 'TIME');
  const timeSatisfied =
    booking.flowType === 'waiting_list'
      ? booking.wlDates.length > 0
      : !!booking.selectedTime;
  const canAdvance = stepIdx <= timeIdx || timeSatisfied;

  // One recovery per mount. Both branches navigate, so without this the effect
  // could re-fire on the render that follows its own navigation.
  const recoveredRef = useRef(false);

  useEffect(() => {
    // NEVER judge a pre-hydration snapshot. BookingProvider starts at
    // DEFAULT_BOOKING_STATE with isLoading=true and hydrates sessionStorage in
    // its OWN effect; React runs a child's effect BEFORE its parent's, so on
    // every fresh mount this effect sees defaults first — no session, no active
    // step — and would take the cold-landing branch for a guest who is about to
    // hydrate into a perfectly valid booking. `recoveredRef` latches, so that
    // misjudgement would be permanent: a plain refresh mid-booking would eject
    // them to `/` with no explanation. Same gate, same reason, as
    // BookingFinalPage's `if (booking.isLoading) return null`.
    if (booking.isLoading) return;
    if (isActive || recoveredRef.current) return;
    recoveredRef.current = true;

    // Both branches are a RECOVERY, not a dead end — the guest is moving again
    // — so they are deliberately not reported through `reportDeadEnd`, whose
    // warning-level Sentry capture must keep meaning "still stuck". They are
    // still recorded: without this, the population PR-3 exists to serve becomes
    // invisible at exactly the moment the residual rate matters. FullStory-only
    // matches the benign-volume precedent in BookingFinalPage (Phase 5).
    // Both warm branches navigate with `replace`: a pushed entry would leave
    // THIS inactive step behind as the previous history entry, so Back
    // remounts it, the fresh mount resets `recoveredRef`, and the guard fires
    // forward again — a loop that makes everything before the dead step
    // unreachable via Back. Same policy, same reason, as the cold branch's
    // `navigate('/', { replace: true })` below.
    if (hasSession && canAdvance) {
      markFullStoryEvent('booking_flow_inactive_step_recovered', {
        step: stepId,
        mode: 'advance',
      });
      goToNextFromStep(stepId, { replace: true });
      return;
    }
    if (hasSession) {
      // Warm, but the guest still owes TIME. Go BACKWARD rather than forward —
      // the same move WaitingListAlternativesPage makes for the counterexample
      // pinned in resolveNextStepAfter's docblock. TIME is always active, and
      // the flow re-derives everything downstream from it.
      markFullStoryEvent('booking_flow_inactive_step_recovered', {
        step: stepId,
        mode: 'back_to_time',
      });
      goToStep('TIME', { replace: true });
      return;
    }
    // Cold landing: redirect rather than a recovery card. That is a product
    // decision taken deliberately for these four mid-funnel steps (Juan,
    // 2026-08-18) — noting that BookingFinalPage's Phase 10 went the other way
    // for /booking-final, where SEO and marketing referrers drive real volume
    // onto a post-conversion URL. If these pages ever show comparable
    // deep-link volume in the event below, revisit that call.
    markFullStoryEvent('booking_flow_inactive_step_recovered', {
      step: stepId,
      mode: 'cold_redirect',
    });
    navigate('/', { replace: true });
  }, [booking.isLoading, isActive, hasSession, canAdvance, stepId, goToNextFromStep, goToStep, navigate]);
}
